'use client';

import Link from 'next/link';
import { PlatformLogo } from '@/components/icons/platform-logo';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { startTransition, useEffect, useMemo, useState } from 'react';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import { PasswordResetForm } from './password-reset-form';
import { PhoneAuthForm } from './phone-auth-form';
import { SignInForm } from './sign-in-form';
import { SignUpForm } from './sign-up-form';
import type { PublicSignupPage } from './types';

type AuthPageProps = {
  authBasePath?: string;
  defaultMode?: 'signIn' | 'signUp';
  signupPage?: PublicSignupPage | null;
  signInPath?: string;
  signUpPath?: string;
};

export function AuthPage({
  authBasePath = '/auth',
  defaultMode = 'signIn',
  signupPage,
  signInPath,
  signUpPath,
}: AuthPageProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocale();
  const copy = getAuthPageCopy(locale);
  const marketingLocalePrefix = locale === 'en' ? '' : `/${locale}`;
  const inviteToken = searchParams.get('invite');
  const presetMode = searchParams.get('mode');
  const returnTo = searchParams.get('returnTo');
  const [mode, setMode] = useState<
    'signIn' | 'signUp' | 'resetPassword' | 'phoneSignIn' | 'phoneSignUp'
  >(
    presetMode === 'signup' || inviteToken
      ? 'signUp'
      : presetMode === 'reset'
        ? 'resetPassword'
        : presetMode === 'signin'
          ? 'signIn'
          : defaultMode
  );

  const currentMode = useMemo(() => {
    const currentInvite = searchParams.get('invite');
    const modeParam = searchParams.get('mode');
    if (modeParam === 'signup' || currentInvite) {
      return 'signUp' as const;
    }
    if (modeParam === 'reset') {
      return 'resetPassword' as const;
    }
    if (modeParam === 'signin') {
      return 'signIn' as const;
    }
    return defaultMode;
  }, [defaultMode, searchParams]);

  useEffect(() => {
    if (mode !== currentMode) {
      startTransition(() => {
        setMode(currentMode);
      });
    }
  }, [mode, currentMode]);

  const resolvedSignInPath = signInPath ?? authBasePath;
  const resolvedSignUpPath = signUpPath ?? authBasePath;

  const buildSigningInParams = (options?: { signupFlow?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.signupFlow) {
      params.set('signupFlow', '1');
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    if (inviteToken) {
      params.set('invite', inviteToken);
    }
    return params;
  };

  const replaceAuthRoute = (
    targetPath: string,
    nextMode: 'signIn' | 'signUp' | 'resetPassword'
  ) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('mode');
    if (nextMode === 'resetPassword') {
      params.set('mode', 'reset');
    } else if (
      (nextMode === 'signIn' && targetPath === authBasePath) ||
      (nextMode === 'signUp' && targetPath === authBasePath)
    ) {
      params.set('mode', nextMode === 'signIn' ? 'signin' : 'signup');
    }
    const query = params.toString();
    router.replace(query ? `${targetPath}?${query}` : targetPath);
  };

  return (
    <div
      data-marketing-theme
      data-auth-page
      className="bg-marketing-surface text-marketing-fg font-marketing max-h-screen min-h-svh overflow-y-auto [font-synthesis:none]"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="flex min-h-svh w-full flex-col p-4 sm:px-8 sm:py-7">
        <header className="flex items-center">
          <Link
            href="/"
            aria-label={copy.goToHomePage}
            className="inline-flex items-center gap-1.5 rounded text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-4"
          >
            <PlatformLogo
              size={18}
              className="text-marketing-fg dark:text-marketing-fg"
            />
            <span>IMAI Studio</span>
          </Link>
        </header>
        <main className="flex flex-1 items-center justify-center py-[clamp(1rem,3svh,3rem)]">
          <div className="flex w-full items-center justify-center">
            <div className="flex w-full max-w-[25rem] flex-col items-center justify-center 2xl:max-w-[38rem]">
              <section className="flex w-full flex-col items-center justify-center">
                {mode === 'signIn' ? (
                  <SignInForm
                    onToggleMode={() => {
                      replaceAuthRoute(resolvedSignUpPath, 'signUp');
                      setMode('signUp');
                    }}
                    onPasswordReset={() => {
                      replaceAuthRoute(resolvedSignInPath, 'resetPassword');
                      setMode('resetPassword');
                    }}
                    onPhoneAuth={() => setMode('phoneSignIn')}
                    inviteToken={inviteToken}
                    authBasePath={authBasePath}
                  />
                ) : mode === 'signUp' ? (
                  <SignUpForm
                    onToggleMode={() => {
                      replaceAuthRoute(resolvedSignInPath, 'signIn');
                      setMode('signIn');
                    }}
                    onPhoneAuth={() => setMode('phoneSignUp')}
                    inviteToken={inviteToken}
                    authBasePath={authBasePath}
                    signupPage={signupPage}
                  />
                ) : mode === 'phoneSignIn' ? (
                  <PhoneAuthForm
                    onBack={() => setMode('signIn')}
                    onSuccess={() => {
                      const params = buildSigningInParams();
                      const query = params.toString();
                      router.push(
                        query ? `/auth/signing-in?${query}` : '/auth/signing-in'
                      );
                    }}
                    isSignUp={false}
                  />
                ) : mode === 'phoneSignUp' ? (
                  <PhoneAuthForm
                    onBack={() => setMode('signUp')}
                    onSuccess={() => {
                      const params = buildSigningInParams({
                        signupFlow: true,
                      });
                      router.push(`/auth/signing-in?${params.toString()}`);
                    }}
                    isSignUp={true}
                  />
                ) : (
                  <PasswordResetForm
                    authBasePath={authBasePath}
                    onBackToSignIn={() => {
                      replaceAuthRoute(resolvedSignInPath, 'signIn');
                      setMode('signIn');
                    }}
                  />
                )}
              </section>
            </div>
          </div>
        </main>

        <footer className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pb-2 text-sm sm:pb-4">
          <Link
            href={`${marketingLocalePrefix}/terms`}
            className="hover:text-foreground transition-colors"
          >
            {copy.termsOfUse}
          </Link>
          <span aria-hidden className="hidden sm:inline">
            |
          </span>
          <Link
            href={`${marketingLocalePrefix}/privacy`}
            className="hover:text-foreground transition-colors"
          >
            {copy.privacyPolicy}
          </Link>
        </footer>
      </div>
    </div>
  );
}
